import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Plus, Trash2, Save, Building2, Home } from 'lucide-react';
import { toast } from 'sonner';
import RoomScheduleTable from './RoomScheduleTable';
import FloorSummaryTable from './FloorSummaryTable';
import ITZATable from './ITZATable';
import ITMSTable from './ITMSTable';
import AccommodationSummaryBar from './AccommodationSummaryBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_OPTIONS = [
  { value: 'residential', label: 'Residential', icon: Home },
  { value: 'commercial', label: 'Commercial', icon: Building2 },
];

const RES_METHODS = [
  { value: 'room_schedule', label: 'Room Schedule' },
  { value: 'floor_summary', label: 'Floor Summary' },
];

const COM_METHODS = [
  { value: 'itza', label: 'ITZA' },
  { value: 'itms', label: 'ITMS' },
  { value: 'floor_summary', label: 'Floor Summary' },
];

export default function AccommodationSchedule({ inspectionId }) {
  const { token } = useContext(AuthContext);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);

  const propertyMode = schedule?.property_mode || 'residential';
  const measurementMethod = schedule?.measurement_method || 'room_schedule';
  const rows = schedule?.rows || [];

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ── Fetch schedule ────────────────────────────────────────────────
  const fetchSchedule = useCallback(async () => {
    try {
      const res = await fetch(`${API}/accommodation/${inspectionId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success && data.schedule) {
        setSchedule(data.schedule);
      }
    } catch (err) {
      console.error('Fetch schedule:', err);
    } finally {
      setLoading(false);
    }
  }, [inspectionId, token]);

  const fetchSummary = useCallback(async () => {
    if (!schedule) return;
    try {
      const res = await fetch(`${API}/accommodation/${inspectionId}/summary`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setSummary(data);
    } catch (err) {
      console.error('Fetch summary:', err);
    }
  }, [inspectionId, token, schedule]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);
  useEffect(() => { fetchSummary(); }, [fetchSummary, rows.length]);

  // ── Create / toggle mode ──────────────────────────────────────────
  const handleCreateOrUpdateMode = async (newMode, newMethod) => {
    setSaving(true);
    try {
      if (!schedule) {
        const res = await fetch(`${API}/accommodation/${inspectionId}`, {
          method: 'POST', headers,
          body: JSON.stringify({ property_mode: newMode, measurement_method: newMethod }),
        });
        const data = await res.json();
        if (data.success) setSchedule(data.schedule);
      } else {
        const res = await fetch(`${API}/accommodation/${inspectionId}/mode`, {
          method: 'PUT', headers,
          body: JSON.stringify({ property_mode: newMode, measurement_method: newMethod }),
        });
        const data = await res.json();
        if (data.success) setSchedule(data.schedule);
      }
    } catch (err) {
      toast.error('Failed to update mode');
    } finally {
      setSaving(false);
    }
  };

  // ── Row CRUD ──────────────────────────────────────────────────────
  const addRow = async (rowData) => {
    try {
      const res = await fetch(`${API}/accommodation/${inspectionId}/rows`, {
        method: 'POST', headers,
        body: JSON.stringify(rowData),
      });
      const data = await res.json();
      if (data.success) {
        setSchedule(prev => ({ ...prev, rows: [...(prev?.rows || []), data.row] }));
        fetchSummary();
      }
    } catch (err) {
      toast.error('Failed to add row');
    }
  };

  const updateRow = async (rowId, rowData) => {
    try {
      const res = await fetch(`${API}/accommodation/${inspectionId}/rows/${rowId}`, {
        method: 'PUT', headers,
        body: JSON.stringify(rowData),
      });
      const data = await res.json();
      if (data.success) {
        setSchedule(prev => ({
          ...prev,
          rows: prev.rows.map(r => r.id === rowId ? data.row : r),
        }));
        fetchSummary();
      }
    } catch (err) {
      toast.error('Failed to update row');
    }
  };

  const deleteRow = async (rowId) => {
    try {
      const res = await fetch(`${API}/accommodation/${inspectionId}/rows/${rowId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setSchedule(prev => ({
          ...prev,
          rows: prev.rows.filter(r => r.id !== rowId),
        }));
        fetchSummary();
      }
    } catch (err) {
      toast.error('Failed to delete row');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="accommodation-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const methods = propertyMode === 'commercial' ? COM_METHODS : RES_METHODS;

  return (
    <div className="space-y-4" data-testid="accommodation-schedule">
      {/* Property Mode Toggle */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
        <span className="text-sm font-medium text-gray-600 w-28">Property Type</span>
        <div className="flex gap-1 bg-white rounded-md border p-0.5">
          {MODE_OPTIONS.map(opt => {
            const Icon = opt.icon;
            const active = propertyMode === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => {
                  const defaultMethod = opt.value === 'commercial' ? 'itza' : 'room_schedule';
                  handleCreateOrUpdateMode(opt.value, defaultMethod);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors ${
                  active ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`mode-toggle-${opt.value}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Measurement Method Toggle */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
        <span className="text-sm font-medium text-gray-600 w-28">Measurement</span>
        <div className="flex gap-1 bg-white rounded-md border p-0.5">
          {methods.map(m => {
            const active = measurementMethod === m.value;
            return (
              <button
                key={m.value}
                onClick={() => handleCreateOrUpdateMode(propertyMode, m.value)}
                className={`px-3 py-1.5 rounded text-sm transition-colors ${
                  active ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`method-toggle-${m.value}`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary bar */}
      {summary && <AccommodationSummaryBar summary={summary} />}

      {/* Table for current method */}
      {measurementMethod === 'room_schedule' && (
        <RoomScheduleTable rows={rows} onAdd={addRow} onUpdate={updateRow} onDelete={deleteRow} />
      )}
      {measurementMethod === 'floor_summary' && (
        <FloorSummaryTable rows={rows} onAdd={addRow} onUpdate={updateRow} onDelete={deleteRow} />
      )}
      {measurementMethod === 'itza' && (
        <ITZATable rows={rows} onAdd={addRow} onUpdate={updateRow} onDelete={deleteRow} />
      )}
      {measurementMethod === 'itms' && (
        <ITMSTable rows={rows} onAdd={addRow} onUpdate={updateRow} onDelete={deleteRow} />
      )}
    </div>
  );
}
